'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useState } from 'react';

/**
 * Renders the chart (SVG or WebP) into a 2400px-wide PNG via canvas, then
 * offers it through the Web Share sheet on mobile or an `<a download>` on
 * desktop. Falls back to opening the source in a new tab.
 *
 * Lives at module scope so its try/finally stays out of the component body,
 * which the React Compiler declines to memoize. Never throws.
 */
async function saveChartImage(src: string, alt: string, filename: string) {
  try {
    // Rasterise the chart (SVG or WebP) to a PNG at a retina target width.
    // Loading the image straight from its URL keeps this format-agnostic.
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = e => reject(e);
      img.src = src;
    });
    const ratio = img.naturalWidth > 0 ? img.naturalHeight / img.naturalWidth : 0.5625;
    // Floor the target at 2400px so SVGs (small intrinsic size) still export
    // crisp; rasters just use their own width if larger.
    const w = Math.max(img.naturalWidth, 2400);
    const h = Math.round(w * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const blob: Blob = await new Promise((resolve, reject) => canvas.toBlob(b => b ? resolve(b) : reject(new Error('canvas')), 'image/png'));

    // Prefer the Web Share API on mobile, gives native share sheet with
    // "Save Image" / "Save to Photos" / "AirDrop" / etc. No new tab needed.
    const file = new File([blob], filename, {
      type: 'image/png'
    });
    const navAny = navigator as Navigator & {
      canShare?: (data: ShareData & {
        files?: File[];
      }) => boolean;
    };
    if (navAny.canShare?.({
      files: [file]
    })) {
      try {
        await navigator.share({
          files: [file],
          title: alt
        });
        return;
      } catch (err) {
        // user cancelled, that's fine, just exit
        if ((err as Error).name === 'AbortError') return;
        // any other error → fall through to classic download
      }
    }

    // Desktop / fallback: classic <a download>. The object URL is held in a
    // local because reading it back off `a.href` yields a re-serialised string
    // that revokeObjectURL won't match, leaking the Blob.
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Yield a macrotask so the browser commits the download before the Blob
    // is freed.
    await new Promise(resolve => setTimeout(resolve, 0));
    URL.revokeObjectURL(objectUrl);
  } catch {
    // Last-ditch fallback: open the chart source in a new tab.
    window.open(src, '_blank', 'noopener');
  }
}

/**
 * Click an SVG chart → download a high-res PNG (web) or trigger the native
 * "Save Image" sheet (iOS Safari long-press hint).
 */
export function DownloadableChart(t0) {
  const $ = _c(16);
  const {
    src,
    alt,
    filename,
    className
  } = t0;
  const [busy, setBusy] = useState(false);
  let t1;
  if ($[0] !== alt || $[1] !== busy || $[2] !== filename || $[3] !== src) {
    t1 = async () => {
      if (busy) {
        return;
      }
      setBusy(true);
      await saveChartImage(src, alt, filename);
      setBusy(false);
    };
    $[0] = alt;
    $[1] = busy;
    $[2] = filename;
    $[3] = src;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const downloadChartImage = t1;
  const t2 = `group relative block w-full cursor-pointer text-left ${className ?? ""}`;
  const t3 = `Download ${alt}`;
  let t4;
  if ($[5] !== alt || $[6] !== src) {
    t4 = <Image src={src} alt={alt} width={1200} height={800} className="w-full border border-[#e2d7c8] bg-white transition group-hover:opacity-90" />;
    $[5] = alt;
    $[6] = src;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = busy ? "Saving\u2026" : "Tap to save";
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="pointer-events-none absolute top-3 right-3 flex items-center gap-1.5 border border-[#24122f] bg-[#fffaf1] px-3 py-1.5 text-[10px] font-black tracking-wider text-[#24122f] uppercase opacity-0 shadow-[3px_3px_0_#24122f] transition group-hover:opacity-100">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== downloadChartImage || $[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t6) {
    t7 = <button type="button" onClick={downloadChartImage} data-press="none" className={t2} aria-label={t3}>{t4}{t6}</button>;
    $[10] = downloadChartImage;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}